import { fuego } from '@store/firebase';

import { updateImgAndGetURL } from '@store/firebase/storage';
import { CollectionPath } from '@typings/collections.enum';
import { IPhotoCard } from '@typings/photocard/photocard.interface';
import { IthemeColorSet } from '@typings/setting/themeColor.interface';
import { statusType } from '@typings/status/statusItem.interface';
import {
  ITEM_FUNCTION_TPYE,
  ITEM_FUNCTION_TPYE_VALUE,
} from '@typings/store/storeItemFunctionType';

export type IUpdateCloset = {
  collectionPath: string;
  closetId: string;
  data: any;
  selectedFile?: File | null;
  preImgURL?: string | null;
  bgFileList?: {
    selectedBgFile?: File | null;
    prevBgImgURL: string | null | undefined;
  };
  fileList: {
    selectedFile?: File | null;
    prevImgURL: string | null | undefined;
  };
  thumbnailFileList?: {
    selectedFile?: File | null;
    prevImgURL: string | null | undefined;
  };
  themeSet?: IthemeColorSet;
};

export async function updateCloset({
  collectionPath,
  closetId,
  themeSet,
  data,
  fileList,
  bgFileList,
  thumbnailFileList,
}: IUpdateCloset) {
  const closetRef = fuego.db
    .collection(`${collectionPath}Closet`)
    .doc(closetId);

  // 이미지
  const imgURL = await updateImgAndGetURL({
    selectedFile: fileList.selectedFile,
    prevImgURL: fileList.prevImgURL,
    collectionPath: `${collectionPath}Closet`,
  });

  // 탭 메뉴 썸네일(선택)
  const thumbnail = thumbnailFileList
    ? await updateImgAndGetURL({
      selectedFile: thumbnailFileList.selectedFile,
      prevImgURL: thumbnailFileList.prevImgURL,
      collectionPath: `${collectionPath}Closet`,
    })
    : undefined;

  if (bgFileList) {
    // 커스텀 테마 배경 설정
    const backgroundImgURL = await updateImgAndGetURL({
      selectedFile: bgFileList.selectedBgFile,
      prevImgURL: bgFileList.prevBgImgURL,
      collectionPath: `${collectionPath}Closet`,
    });

    return closetRef.update({
      ...data,
      themeSet: {
        ...themeSet,
        backgroundImgURL,
      },
      editedAt: new Date(),
      imgURL,
      ...(thumbnailFileList ? { thumbnail } : {}),
    });
  } else {
    // 배경꾸미기 없이 이 탭 저장 할 때
    return closetRef.update({
      ...data,
      editedAt: new Date(),
      imgURL,
      ...(thumbnailFileList ? { thumbnail } : {}),
    });
  }
}

export async function updatePhotoCardOrder({ collectionPath, orderList }) {
  const photoCardRef = fuego.db.collection(collectionPath);

  return orderList.map((list, i) => {
    return photoCardRef.doc(list.id).update({
      order: i,
    });
  });
}

/**
 * 인벤토리 그리드 뷰 슬롯 위치(slotIndex) 일괄 업데이트
 * items: slotIndex가 변경된 아이템 배열 (slotIndex=undefined → 미배치)
 */
export async function updateInventorySlotIndex({
  collectionPath,
  items,
}: {
  collectionPath: string;
  items: { id: string; slotIndex?: number }[];
}) {
  const ref = fuego.db.collection(collectionPath);
  return items.map((item) =>
    ref.doc(item.id).update({
      slotIndex: item.slotIndex ?? null,
    }),
  );
}

type IphotocardProps = {
  data: any;
  collectionPath: string;
  fileList?: {
    selectedFile?: File | null;
    prevImgURL: string | null | undefined;
    hoverSelectedFile?: File | null;
    hoverPrevImgURL?: string | null | undefined;
  };
  docId?: string;
  statusId?: statusType;
  functionType?: ITEM_FUNCTION_TPYE_VALUE;
  updateStatusValue?: string | number;
  currentStatusField?: string; // 현재 스탯 필드명 (currentHp, currentMp 등)
};

export async function updatePhotoCard({
  data,
  collectionPath,
  fileList,
  docId,
  statusId,
  functionType = ITEM_FUNCTION_TPYE.DEFAULT,
  updateStatusValue,
  currentStatusField,
}: IphotocardProps) {
  const ref = fuego.db.collection(collectionPath);
  // 기존이미지는 삭제하고, 신규이미지는 추가

  let thumbnail = fileList?.prevImgURL || '';
  // 기존 이미지 url이 없으면, 다시 한 번 포토카드를 검토해서 imgURL을 받아오기

  if (!fileList || !fileList?.prevImgURL) {
    await ref
      .doc(docId)
      .get()
      .then((res) => {
        const prevData = res.data();
        if (!prevData) return;
        // prevImgURL을 업데이트 해준다
        thumbnail = prevData.thumbnail;
      });
  }

  if (fileList) {
    const { selectedFile, prevImgURL, hoverSelectedFile, hoverPrevImgURL } = fileList as any;
    thumbnail = await updateImgAndGetURL({
      selectedFile,
      prevImgURL,
      collectionPath,
    });

    // optional hover thumbnail
    const hoverThumbnail = await updateImgAndGetURL({
      selectedFile: hoverSelectedFile || null,
      prevImgURL: hoverPrevImgURL || null,
      collectionPath,
    });

    if (hoverThumbnail) {
      data = { ...data, hoverThumbnail };
    }
  }

  // 스탯 보정 아이템인 경우, 현재 스탯을 조정함 (data 병합 전에 처리)
  if (functionType === ITEM_FUNCTION_TPYE.STATUS && statusId && currentStatusField && updateStatusValue !== undefined) {
    console.log('현재 스탯 업데이트:', {
      statusId,
      currentStatusField,
      updateStatusValue,
    });

    // 최대값 가져오기 (data에서 또는 기존 데이터에서)
    const maxValue = Number(data[statusId]) || 0;
    const finalValue = Math.min(Number(updateStatusValue) || 0, maxValue);

    // data 객체에 현재 스탯 필드 추가
    data = {
      ...data,
      [currentStatusField]: finalValue,
    };
  }

  let newData: IPhotoCard = {
    ...data,
    thumbnail,
    editedAt: new Date(),
    __snapshot: '',
  };

  return ref
    .doc(docId)
    .set(newData, { merge: true })
    .catch((err) => console.log(err.message));
}

export function updateToggleCloset({
  isChecked,
  closetId,
  collectionPath,
  key,
}) {
  const closetRef = fuego.db
    .collection(`${collectionPath}Closet`)
    .doc(closetId);

  return closetRef.set({ [key]: isChecked }, { merge: true });
}

export function updateTabList({ closetId, collectionPath, tabId, data }) {
  // data: IClosetTab
  const closetRef = fuego.db
    .collection(`${collectionPath}Closet`)
    .doc(closetId)
    .collection('tabList');
  if (tabId) {
    //수정
    return closetRef.doc(tabId).set(data, { merge: true });
  }

  if (!tabId) {
    //추가
    return closetRef.add(data);
  }
}

export async function getPhotocardIDbyUser({ uid }: { uid: string }): Promise<string | undefined> {
  // REPRESENTATIVE_CHARACTER_RULE 우선순위:
  // 1순위: users/{uid}.representativePhotocardPath + representativePhotocardId
  // 2순위: 목록의 첫 번째 포토카드 (fallback)
  const userSnap = await fuego.db
    .collection(CollectionPath.USERS)
    .doc(uid)
    .get();

  if (userSnap.exists) {
    const userData = userSnap.data() as any;
    if (userData?.representativePhotocardId) {
      return userData.representativePhotocardId as string;
    }
  }

  // fallback: 목록의 첫 번째 포토카드
  const snap = await fuego.db
    .collection(`${CollectionPath.PHOTOCARD}`)
    .where('owner', '==', uid)
    .get();

  return snap.docs[0]?.id;
}

export async function updateCamp({ campID, photocardID }) {

  return fuego.db
    .collection(`${CollectionPath.PHOTOCARD}`)
    .doc(photocardID)
    .set({ camp: campID }, { merge: true });
}

export async function updatePhotoCardOwner({ 
  collectionPath, 
  docId, 
  owner 
}: { 
  collectionPath: string; 
  docId: string; 
  owner: string;
}) {
  return fuego.db
    .collection(collectionPath)
    .doc(docId)
    .set({ owner, editedAt: new Date() }, { merge: true });
}
